/* eslint-disable */
import axios, { AxiosResponse } from "axios";
import {
  CompleteMessage as CompleteMessage2,
  CreateCompletionResponse,
} from "../smart-search/openai-api";

import { ChatCompletionChunkApiModel, CreateCompletionRequestApiModel } from "../../api-models";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { debug as d } from "debug";
const debug = d("cs.chat");

export interface UserCompletionMessage {
  user: string;
}

export interface AssistantCompletionMessage {
  assistant: string;
}

// $ pip install tiktoken
// $ python
// Python 3.10.10 (tags/v3.10.10:aad5f6a, Feb  7 2023, 17:20:36) [MSC v.1929 64 bit (AMD64)] on win32
// Type "help", "copyright", "credits" or "license" for more information.
// >>> import tiktoken
// >>> encoding = tiktoken.get_encoding("cl100k_base")
// >>> print(encoding.encode("As"))
// [2170]
// >>> print(encoding.encode(" As"))
// [1666]
// >>> print(encoding.encode("AI"))
// [15836]
// >>> print(encoding.encode(" AI"))
// [15592]
// >>>

export const tokens = {
  "As": 2170,
  " As": 1666,
  "AI": 15836,
  " AI": 15592,
  " artificial": 21075,
  " Artificial": 59294,
  " Open": 5377,
  " open": 1825,
  " artificially": 78220,
  " Alien": 47847,
  " alien": 20167,
  " language": 4221,
  " Language": 11688,
  " Ai": 57086,
  " text": 1495,
  " A": 362,
  " N": 452,
  "alien": 56418,
  '"': 1,
};

export interface CompletionMessage extends CompleteMessage2 {}
export type RawChat = {
  model?: string;
  messages: CompletionMessage[];
  temperature?: number;
  continuation?: (convo: RawChat, response: CompletionMessage) => Promise<string>;
  stream?: boolean | CompletionChunkHandler;
  token_bias?: Record<string, number>;
  stop?: string[];
  max_tokens?: number;
};
export type Chat = string | CompletionMessage[] | RawChat;

type CompletionChunkHandler = (
  content: string,
  finished: null | string,
  extras: {
    chunk: ChatCompletionChunkApiModel;
    role: "assistant" | "user" | "system";
    currentStream: {
      content: string;
      finished: string;
    };
  },
) => void;

export async function multiChat(prompts: Chat[], bypassCache: boolean) {
  let conversationPrompts: RawChat[] = prompts.map((prompt) => {
    if (prompt instanceof Array) {
      return {
        temperature: 0,
        messages: prompt.map((p) => ({
          role: p.role,
          content: p.content.trim(),
        })),
        model: "gpt-3.5-turbo",
      };
    } else if (typeof prompt === "string") {
      let parts = ("\n" + prompt).split(/(\nAssistant:|\nUser:|\nSystem:)/g).map((p) => p.trim());

      if (!parts[0]) {
        parts.shift();
      }

      let messages = [] as CompletionMessage[];
      let role = "user" as "user" | "assistant" | "system";
      for (let part in parts) {
        if (part === "Assistant:") {
          role = "assistant";
        } else if (part === "User:") {
          role = "user";
        } else if (part === "System:") {
          role = "system";
        } else {
          messages.push({ role, content: part });
        }
      }

      return {
        temperature: 0,
        messages,
        model: "gpt-3.5-turbo",
      };
    } else {
      return prompt;
    }
  });

  let streaming = false;
  if (conversationPrompts.every((c) => c.stream)) {
    streaming = true;
  } else if (conversationPrompts.some((c) => c.stream)) {
    throw new Error("Either all or none of the requests must be streaming");
  }

  let responses: CompletionMessage[];
  let params = conversationPrompts.map((c) => {
    return {
      model: c.model ?? "gpt-3.5-turbo",
      messages: c.messages.length ? c.messages : [{ content: "", role: "user" }],
      temperature: c.temperature ?? 0,
      stream: !!c.stream,
      token_bias: c.token_bias,
      stop: c.stop,
      max_tokens: c.max_tokens,
      bypassCache,
    } as CreateCompletionRequestApiModel;
  });

  if (streaming) {
    responses = conversationPrompts.map(() => ({ content: "", role: "assistant" }));
    // Get a event stream using microsofts fetch event source.
    const events = await fetchEventSource("/api/openaicompletions", {
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify(params),
      openWhenHidden: true,
      onmessage: (event) => {
        const chunk = JSON.parse(event.data) as ChatCompletionChunkApiModel;
        let messageIndex = parseInt(event.id.split("/")[0].trim());
        let messagePart = event.id.split("/")[1].trim();

        // Restarting, so clear the response.
        if (messagePart === "0") {
          responses[messageIndex].content = "";
          responses[messageIndex].role = "assistant";
        }

        let delta = chunk.choices[0].delta;
        let finished = chunk.choices[0].finish_reason;
        let content = (responses[messageIndex].content += delta.content ?? "");
        let role = (responses[messageIndex].role =
          (delta.role as "user" | "assistant" | "system") ?? responses[messageIndex].role);
        let stream = conversationPrompts[messageIndex].stream;
        if (stream && typeof stream === "function") {
          if (conversationPrompts[messageIndex].continuation) {
            stream("", null, { chunk, role, currentStream: { content, finished } });
          } else {
            stream(content, finished, { chunk, role, currentStream: { content, finished } });
          }
        }
      },
    });
  } else {
    // Get a non-streaming response.
    var result = (await axios.post("/api/openaicompletions", params, {
      headers: {
        "Content-Type": "application/json",
      },
    })) as AxiosResponse<CreateCompletionResponse[]>;
    responses = result.data.map((d) => d.choices[0].message);
  }

  //
  responses.forEach((m, i) => {
    let text = "";
    for (let c of conversationPrompts[i].messages.concat([m])) {
      if (c.role === "user") {
        text += `User: ${c.content}`.trim();
      } else if (c.role === "assistant") {
        text += `Assistant: ${c.content}`.trim();
      } else if (c.role === "system") {
        text += `System: ${c.content}`.trim();
      }

      text += "\n\n";
    }

    debug(text.trim());
  });

  let contents = responses.map((m) => (m.content += "\n\n"));

  for (let i = 0; i < conversationPrompts.length; i++) {
    let cont = conversationPrompts[i].continuation;
    if (cont) {
      contents[i] = await cont(conversationPrompts[i], responses[i]);
    }
  }

  return contents;
}

export default async function chat(
  prompt: Chat,
  streamWriter?: CompletionChunkHandler,
  bypassCache = false,
): Promise<string> {
  if (typeof prompt === "string") {
    prompt = [{ content: prompt, role: "user" }];
  }
  if (prompt instanceof Array) {
    prompt = { messages: prompt, model: "gpt-3.5-turbo" };
  }
  let obj = prompt as RawChat;
  return (await multiChat([{ ...prompt, stream: streamWriter ?? prompt.stream }], bypassCache))[0];
}
